// src/pages/ApplicationChampionship/components/ChampionshipFilterBox/index.tsx
import { Box, Button, Flex, Heading, Input, InputGroup, Select, Text } from '@chakra-ui/react';
import { useController, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ChampionshipFilters } from '../../types';
import { InputField } from '@/components/InputField';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';

export interface ChampionshipFilterBoxProps {
  filtersForm: UseFormReturn<ChampionshipFilters>;
}

export const ChampionshipFilterBox = ({ filtersForm }: ChampionshipFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

    const startDateControl = useController({
      name: 'start_date',
      control,
    });
    const endDateControl = useController({
      name: 'end_date',
      control,
    });

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_championship.components.championship_filter_box.championship_filter_box_title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <Flex gap='deka' alignItems='flex-end' flexWrap='wrap' w='100%'>
          {/* Nome do campeonato */}
          <InputGroup display='flex' flexDirection='column' maxW={{ base: '100%', md: '280px' }}>
            <Text fontWeight='medium'>
              {t('pages.application_championship.components.championship_filter_box.championship_filter_box_name')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('name'))} />
          </InputGroup>

          {/* Filtro: ativo / inativo */}
          <InputGroup display='flex' flexDirection='column' maxW={{ base: '100%', md: '180px' }}>
            <Text fontWeight='medium'>
              {t('pages.application_championship.components.championship_filter_box.championship_filter_box_is_active')}
            </Text>
            <Select variant='flushed' {...register('is_active')}>
              <option value=''>
                {t('pages.application_championship.components.championship_filter_box.championship_filter_box_all')}
              </option>
              <option value='true'>
                {t('pages.application_championship.components.championship_filter_box.championship_filter_box_active')}
              </option>
              <option value='false'>
                {t('pages.application_championship.components.championship_filter_box.championship_filter_box_inactive')}
              </option>
            </Select>
          </InputGroup>

          {/* Filtro: filtrado / não filtrado */}
          <InputGroup display='flex' flexDirection='column' maxW={{ base: '100%', md: '200px' }}>
            <Text fontWeight='medium'>
              {t('pages.application_championship.components.championship_filter_box.championship_filter_box_is_filtered')}
            </Text>
            <Select variant='flushed' {...register('is_filtered')}>
              <option value=''>
                {t('pages.application_championship.components.championship_filter_box.championship_filter_box_all')}
              </option>
              <option value='true'>
                {t('pages.application_championship.components.championship_filter_box.championship_filter_box_filtered')}
              </option>
              <option value='false'>
                {t('pages.application_championship.components.championship_filter_box.championship_filter_box_not_filtered')}
              </option>
            </Select>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column' maxW={{ base: '100%', md: '200px' }}>
            <Text fontWeight='medium'>
              {t('pages.application_championship.components.championship_filter_box.championship_filter_box_start_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(startDateControl.field.value)}
              onChange={event => startDateControl.field.onChange(event.target.value)}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column' maxW={{ base: '100%', md: '200px' }}>
            <Text fontWeight='medium'>
              {t('pages.application_championship.components.championship_filter_box.championship_filter_box_end_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(endDateControl.field.value)}
              onChange={event => endDateControl.field.onChange(event.target.value)}
              min={formatDateToDateInputValue(startDateControl.field.value)}
            />
          </InputGroup>

        </Flex>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_championship.components.championship_filter_box.championship_filter_box_clear')}
        </Button>
      </Flex>
    </Box>
  );
};
